import { Cubit } from "blac-next";
import type {
  DropdownActionSheetProps,
  DropdownActionSheetState
} from "./DropdownActionSheet.types";
import {
  calculateHighlightPosition,
  calculatePlacement,
  flattenButtons
} from "./DropdownActionSheet.utils";
import { DEFAULT_TRIGGER_POSITION } from "./DropdownActionSheet.constants";

const HIGHLIGHT_TRANSITION_POSITION =
  "top 180ms cubic-bezier(0.05, 0.9, 0.2, 1), height 180ms cubic-bezier(0.05, 0.9, 0.2, 1), opacity 150ms ease-out";

const HIGHLIGHT_TRANSITION_FADE = "opacity 150ms ease-out";

const createInitialState = (
  props: DropdownActionSheetProps
): DropdownActionSheetState => ({
  open: props.isOpen,
  placement: "bottom start",
  title: props.options.title,
  message: props.options.message,
  buttons: props.options.buttons,
  iconPosition: props.options.iconPosition ?? "left"
});

export class DropdownActionSheetBloc extends Cubit<
  DropdownActionSheetState,
  DropdownActionSheetProps
> {
  static isolated = true;

  private isFirstHover = true;
  private highlightElement: HTMLDivElement | null = null;
  private menuElement: HTMLDivElement | null = null;
  /**
   * Toggling a switch re-renders the menu, which causes react-aria to fire
   * onOpenChange(false) even though the sheet should stay open. This flag is
   * set before the patch and consumed (and reset) in handleOpenChange to
   * suppress that spurious dismiss.
   */
  private _skipNextDismiss = false;

  props: DropdownActionSheetProps;

  constructor(
    props: DropdownActionSheetProps = {
      isOpen: false,
      onDidDismiss: () => {},
      options: { buttons: [] }
    }
  ) {
    super(createInitialState(props));
    this.props = props;

    if (this.state.open) {
      const position = props.triggerPosition ?? DEFAULT_TRIGGER_POSITION;
      const { placement } = calculatePlacement(position, props.options);
      this.patch({ placement });
    }
  }

  get backdropDismiss(): boolean {
    return this.props.options.backdropDismiss ?? true;
  }

  get cancelButtonId(): string {
    return this.props.options.cancelButtonId ?? "cancel";
  }

  updateProps = (newProps: DropdownActionSheetProps): void => {
    this.props = newProps;
    this.patch(createInitialState(newProps));
  };

  setElements = (
    highlightElement: HTMLDivElement | null,
    menuElement: HTMLDivElement | null
  ): void => {
    this.highlightElement = highlightElement;
    this.menuElement = menuElement;
  };

  updatePosition = (position: { x: number; y: number }): void => {
    const { placement } = calculatePlacement(position, this.props.options);
    this.patch({ placement });
  };

  updateHighlight = (itemElement: HTMLElement | null): void => {
    if (!this.highlightElement) {
      return;
    }

    const position = calculateHighlightPosition(itemElement, this.menuElement);

    if (!position) {
      this.highlightElement.style.opacity = "0";
      return;
    }

    // First hover: fade in without position animation
    // Subsequent hovers: animate position
    const transition = this.isFirstHover
      ? HIGHLIGHT_TRANSITION_FADE
      : HIGHLIGHT_TRANSITION_POSITION;

    if (this.isFirstHover) {
      this.isFirstHover = false;
    }

    this.highlightElement.style.transition = transition;
    this.highlightElement.style.top = `${position.top}px`;
    this.highlightElement.style.height = `${position.height}px`;
    this.highlightElement.style.opacity = "1";
  };

  hideHighlight = (): void => {
    this.isFirstHover = true;
    if (this.highlightElement) {
      this.highlightElement.style.opacity = "0";
      this.highlightElement.dataset.pressed = "false";
    }
  };

  setHighlightPressed = (pressed: boolean): void => {
    if (this.highlightElement) {
      this.highlightElement.dataset.pressed = pressed ? "true" : "false";
    }
  };

  dismiss = (): void => {
    if (!this.backdropDismiss) {
      return;
    }
    this.patch({ open: false });
    this.props.onDidDismiss(this.cancelButtonId);
  };

  selectOption = (buttonId: string): void => {
    this.patch({ open: false });
    this.props.onDidDismiss(buttonId);
  };

  consumeSkipDismiss = (): boolean => {
    const skip = this._skipNextDismiss;
    this._skipNextDismiss = false;
    return skip;
  };

  toggleButton = (buttonId: string): void => {
    // Search the full button tree (top-level + submenu children) for the toggle.
    type Btn = (typeof this.state.buttons)[number];
    const button = flattenButtons(this.state.buttons).find(
      (b) => b.id === buttonId
    );
    if (!button) {
      return;
    }
    this._skipNextDismiss = true;
    const newValue = !button.toggled;

    const updateButtons = (buttons: readonly Btn[]): Btn[] =>
      buttons.map((b) => {
        if (b.id === buttonId) {
          return { ...b, toggled: newValue };
        }
        if (b.children?.length) {
          return { ...b, children: updateButtons(b.children) };
        }
        return b;
      });

    this.patch({ buttons: updateButtons(this.state.buttons) });
    this.props.options.onToggle?.(buttonId, newValue);
  };
}
